//  ----------      START   IMPORT  BLOCK       ----------
import React, { useState, useEffect, useCallback } from "react";
//  Widgets
import { Form, InputGroup, ListGroup, Spinner } from "react-bootstrap";
//  Context
import { useLanguage } from "../../API/Context/LanguageContext";
//  Config
import VocabWhitelistForm, {
  type VocabWhitelistFormKey,
} from "../../ConfigFiles/multilingual/VocabWhitelistForm";
//  ----------      END   IMPORT  BLOCK       ----------

export interface WhitelistUser {
  username: string;
  name: string;
}

interface WhitelistUserSearchBarProps {
  /** Function to handle selection of a user */
  onUserFound: (user: WhitelistUser) => void;
  /** Async search function passed from parent component */
  onSearch: (query: string) => Promise<string | null>;
  /** Optional custom label for the search input */
  label?: string;
  /** Initial query value */
  initialValue?: string | undefined;
  /** Whether to clear the search input after a user is selected (default: true) */
  clearOnSelect?: boolean;
}

const WhitelistUserSearchBar: React.FC<WhitelistUserSearchBarProps> = ({
  onUserFound,
  onSearch,
  label,
  initialValue = "",
  clearOnSelect = true,
}) => {
  const { lan } = useLanguage();

  const [searchTerm, setSearchTerm] = useState<string>(initialValue);
  const [searching, setSearching] = useState<boolean>(false);
  const [result, setResult] = useState<WhitelistUser | null>(null);
  const [error, setError] = useState<string | null>(null);

  const getVocab = useCallback(
    (key: VocabWhitelistFormKey, defaultText = key): string => {
      return VocabWhitelistForm[key]?.[lan] ?? defaultText;
    },
    [lan]
  );

  const handleSearch = useCallback(
    async (value: string) => {
      setSearchTerm(value);
      setResult(null);
      setError(null);

      if (!value || value.trim() === "") {
        setSearching(false);
        return;
      }

      setSearching(true);
      try {
        // Execute the search method passed via props
        const fullname = await onSearch(value);

        if (fullname && fullname.trim() !== "") {
          setResult({ username: value, name: fullname });
        } else {
          setError(getVocab("no_matches"));
        }
      } catch (err) {
        console.error("Error searching user:", err);
        setError(getVocab("impossible_to_search"));
      } finally {
        setSearching(false);
      }
    },
    [getVocab, onSearch]
  );

  useEffect(() => {
    if (initialValue) {
      handleSearch(initialValue);
    }
  }, [initialValue, handleSearch]);

  const handleSelectUser = () => {
    if (!result) return;
    onUserFound?.(result);
    if (clearOnSelect) {
      setSearchTerm("");
      setResult(null);
    }
  };

  const renderStatusArea = () => {
    if (searching) {
      return (
        <div className="mt-2">
          <Spinner size="sm" animation="border" /> {getVocab("searching")}
        </div>
      );
    }

    if (result) {
      return (
        <ListGroup className="mt-2">
          <ListGroup.Item action onClick={handleSelectUser}>
            ➕ {result.name}{" "}
            <span className="username-highlight">@{result.username}</span>
          </ListGroup.Item>
        </ListGroup>
      );
    }

    if (error) {
      return (
        <ListGroup className="mt-2">
          <ListGroup.Item disabled>{error}</ListGroup.Item>
        </ListGroup>
      );
    }

    if (!searchTerm || searchTerm.trim() === "") {
      return (
        <ListGroup className="mt-2">
          <ListGroup.Item disabled className="text-muted border-0">
            {getVocab("search_hint")}
          </ListGroup.Item>
        </ListGroup>
      );
    }

    return null;
  };

  return (
    <div>
      <Form.Label>{label || getVocab("network_username")}</Form.Label>
      <InputGroup>
        <Form.Control
          type="text"
          placeholder={getVocab("search_by_username_placeholder")}
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </InputGroup>

      {renderStatusArea()}
    </div>
  );
};

export default WhitelistUserSearchBar;